import { Link, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { ArrowRight, ShieldCheck, Sparkles, BrainCircuit } from "lucide-react";

export default function Welcome() {
  const nav = useNavigate();
  const [entering, setEntering] = useState(false);
  // Portal-walk transition: on Enter, zoom the whole scene into the pillar
  // while fading, then navigate. Users who set prefers-reduced-motion skip
  // straight to /register with no animation.
  function handleEnter() {
    const reduced = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { nav("/register"); return; }
    setEntering(true);
    setTimeout(() => nav("/register"), 550);
  }
  // Starfield — memoized so the random positions are stable across renders.
  // ~55 stars is enough density without any perf cost (each is a 1-2px div,
  // opacity-only animation, no layout thrash).
  const stars = useMemo(() => Array.from({ length: 55 }, (_, i) => ({
    id: i,
    top:      Math.random() * 100,           // 0-100 %
    left:     Math.random() * 100,           // 0-100 %
    size:     Math.random() < 0.75 ? 1 : 2,  // most are 1px, a few 2px
    delay:    Math.random() * 6,             // 0-6s
    duration: 3 + Math.random() * 4,         // 3-7s cycle
    warm:     Math.random() < 0.15,          // a small % are subtle violet, rest are white
  })), []);
  return (
    // The whole landing page must fit in one viewport on any device — phone,
    // tablet, or desktop — without scrolling. We use `100dvh` (not 100vh) so
    // iOS Safari's dynamic address-bar behavior can't push content below the
    // fold, and every vertical measurement is compact-on-mobile / roomy-on-
    // desktop via sm: breakpoints.
    <div className={`relative h-[100dvh] overflow-hidden ${entering ? "welcome-portal-enter" : ""}`}>
      {/* Ambient gradient blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-rose-600/10 blur-3xl" />

      {/* Ambient starfield — sits between the gradient blobs and the main
          content column. `mix-blend-mode: screen` makes each star's light
          pixel-add to whatever's underneath so they look like they're floating
          above the aurora rather than punched onto it. Randomized twinkle
          delays keep the field organic. */}
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ mixBlendMode: "screen" }}>
        {stars.map((s) => (
          <span
            key={s.id}
            className="welcome-star"
            style={{
              top: `${s.top}%`,
              left: `${s.left}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              background: s.warm ? "rgba(196, 145, 253, 0.9)" : "rgba(255, 255, 255, 0.85)",
              boxShadow: s.warm
                ? "0 0 4px rgba(196, 145, 253, 0.5)"
                : "0 0 4px rgba(255, 255, 255, 0.35)",
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto flex h-[100dvh] max-w-md flex-col px-6 pt-safe pb-safe">
        {/* No top header — the pillar banner IS the top of the screen. The
            18+ acknowledgement lives down by the Sign-in link instead so
            nothing competes with the artwork. */}

        {/* Vertically centered main block — grows to consume free space so the
            layout stays balanced on tall screens without pushing anything off. */}
        <main className="flex flex-1 flex-col justify-center min-h-0 py-4">
          {/* Hero banner — the "IMMERSIVE — Step inside the story" wordmark
              next to the sacred-geometry pillar. Blend stack to kill the
              "picture in a box" feel:
                1. `mix-blend-mode: screen` — near-black background of the
                   image pixel-adds with the page bg → dark parts disappear.
                2. Two intersecting `mask-image` gradients — soft horizontal
                   fade at LEFT/RIGHT (no vertical seam) and a soft vertical
                   fade at the BOTTOM so the artwork melts into the feature
                   rows below.
                3. Negative `-mx-6` breaks the image out of the parent's `px-6`
                   padding so it can span the full viewport width on mobile. */}
          {/* Pillar + wordmark hero. Wrapped so we can layer a subtle
              pulsing glow OVER the pillar's central star (right side of
              image, ~65% from the left). The overlay uses `mix-blend-mode:
              screen` so it adds to the artwork's existing glow rather than
              covering it.  */}
          <div className="relative -mx-6 w-[calc(100%+3rem)] -mt-2 sm:-mt-4" data-testid="welcome-hero">
            {/* Living-portal pulse — 4.5s ease-in-out breath on the pillar's
                central star. `pointer-events-none` keeps it purely visual. */}
            <div
              aria-hidden
              className="welcome-pillar-pulse pointer-events-none absolute z-10"
              style={{
                top: "20%",
                left: "60%",
                width: "35%",
                height: "70%",
                background:
                  "radial-gradient(circle at center, rgba(196, 145, 253, 0.55) 0%, rgba(139, 92, 246, 0.25) 35%, transparent 70%)",
                filter: "blur(20px)",
                mixBlendMode: "screen",
              }}
            />
            <img
              src="/immersive-banner-hero.webp"
              alt="Immersive — Step inside the story"
              data-testid="welcome-hero-banner"
              className="relative block w-full max-w-none h-auto max-h-[38vh] object-contain"
              style={{
                mixBlendMode: "screen",
                maskImage:
                  "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%), linear-gradient(to bottom, black 0%, black 60%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%), linear-gradient(to bottom, black 0%, black 60%, transparent 100%)",
                maskComposite: "intersect",
                WebkitMaskComposite: "source-in",
              }}
              loading="eager"
              decoding="async"
            />
          </div>

          {/* Positioned so the first card sits just BELOW the stairs at the
              base of the pillar (not overlapping the artwork itself). */}
          <div className="mt-2 sm:mt-3 space-y-2 sm:space-y-3">
            <FeatureRow icon={BrainCircuit} title="Better memory" text="Pinned facts, chat history, and auto-summary keep the story consistent." />
            <FeatureRow icon={Sparkles} title="Claude Sonnet 4.6" text="Cinematic prose. Rich characters. Roleplay that actually reads well." />
            <FeatureRow icon={ShieldCheck} title="SFW & NSFW" text="Per-character toggle. Adult chat style unlocks only on 18+ bots." />
          </div>
        </main>

        <footer className="shrink-0 pb-1 sm:pb-2">
          <button
            data-testid="welcome-register-btn"
            onClick={handleEnter}
            disabled={entering}
            className="btn-pill btn-primary w-full text-base py-3 disabled:opacity-100"
          >
            Enter — I'm 18 or older <ArrowRight size={18} />
          </button>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-neutral-500">
            <span className="nsfw-badge" data-testid="welcome-18plus-badge">18+</span>
            <span>Already have an account?</span>
            <Link to="/login" data-testid="welcome-login-link" className="text-violet-400 hover:text-violet-300">
              Sign in
            </Link>
          </p>
          <p className="mt-2 text-center text-[10px] leading-tight text-neutral-600">
            By entering you confirm you are at least 18 years old and consent to mature content.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[10px] text-neutral-600">
            <Link to="/pricing" className="hover:text-violet-400" data-testid="welcome-pricing-link">Pricing</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-violet-400" data-testid="welcome-terms-link">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-violet-400" data-testid="welcome-privacy-link">Privacy</Link>
            <span>·</span>
            <Link to="/refunds" className="hover:text-violet-400" data-testid="welcome-refunds-link">Refunds</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-violet-400" data-testid="welcome-contact-link">Contact</Link>
          </div>
        </footer>
      </div>

      {/* Portal-walk flash overlay — rendered only during the entering
          transition. Direct child of the root (NOT nested inside the
          zooming main content column) so its own animation is not fought
          by the parent's opacity → 0. Fades in a bright violet burst at
          the pillar's location so it feels like walking THROUGH the portal,
          not just watching a zoom-in. */}
      {entering && (
        <div
          aria-hidden
          className="welcome-portal-flash pointer-events-none absolute inset-0 z-40"
          style={{
            background:
              "radial-gradient(circle at 70% 30%, rgba(196,145,253,0.95) 0%, rgba(139,92,246,0.7) 20%, rgba(11,10,16,0) 60%)",
            mixBlendMode: "screen",
          }}
        />
      )}

      <style>{`
        /* Living-portal pulse — soft 4.5s breath overlaid on the pillar's
           central star. Reduced motion users get a static glow. */
        @keyframes welcome-pillar-pulse {
          0%, 100% { opacity: 0.35; transform: scale(0.88); }
          50%      { opacity: 0.95; transform: scale(1.12); }
        }
        .welcome-pillar-pulse {
          transform-origin: center center;
          animation: welcome-pillar-pulse 4.5s ease-in-out infinite;
        }
        /* Starfield twinkle — opacity-only, no layout/paint churn beyond
           compositing. Individual duration + delay set inline for organic
           variety. */
        @keyframes welcome-star-twinkle {
          0%, 100% { opacity: 0.15; }
          50%      { opacity: 0.9; }
        }
        .welcome-star {
          position: absolute;
          border-radius: 9999px;
          animation-name: welcome-star-twinkle;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          will-change: opacity;
        }
        /* Portal-walk transition. Zooms the whole scene INTO the pillar
           (transform-origin lines up with the pillar on the banner) while
           fading + slight blur → feels like being pulled through a doorway.
           Composited on GPU (transform + opacity + filter). */
        @keyframes welcome-portal-enter {
          0%   { opacity: 1; transform: scale(1);    filter: blur(0);   }
          60%  { opacity: 0.9;                       filter: blur(2px); }
          100% { opacity: 0; transform: scale(2.4);  filter: blur(8px); }
        }
        .welcome-portal-enter > *:not(.welcome-portal-flash) {
          transform-origin: 70% 30%;
          animation: welcome-portal-enter 550ms cubic-bezier(0.65, 0, 0.35, 1) forwards;
        }
        /* The white flash fades IN over the same window so it peaks at the
           moment the scene has zoomed past the "threshold". */
        @keyframes welcome-portal-flash {
          0%   { opacity: 0; }
          70%  { opacity: 0.9; }
          100% { opacity: 1; }
        }
        .welcome-portal-flash {
          animation: welcome-portal-flash 550ms ease-in forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .welcome-pillar-pulse { animation: none; opacity: 0.55; }
          .welcome-star { animation: none; opacity: 0.5; }
          .welcome-portal-enter > *,
          .welcome-portal-flash { animation: none; }
        }
      `}</style>
    </div>
  );
}

function FeatureRow({ icon: Icon, title, text }) {
  // Tight padding + icon size on small phones; roomier from sm: up.
  return (
    <div className="flex gap-3 rounded-2xl border border-white/5 bg-white/[0.02] p-3 sm:gap-4 sm:p-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-300 sm:h-10 sm:w-10">
        <Icon size={18} className="sm:hidden" />
        <Icon size={20} className="hidden sm:block" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-white sm:text-base">{title}</div>
        <div className="mt-0.5 text-xs text-neutral-400 leading-snug sm:text-sm">{text}</div>
      </div>
    </div>
  );
}
